import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer } from "recharts";
import sh from "bundles/common/utils/sh";
import ProfileDetailPanel from "bundles/Internal/components/common/ProfileDetailPanel";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

const COLORS = {
  background: "hsl(140, 10%, 95%)",
  bg2: "#ffffff",
  bg3: "hsl(140, 8%, 90%)",
  foreground: "hsl(150, 30%, 12%)",
  primary: "hsl(150, 38%, 17%)",
  mutedForeground: "hsl(150, 10%, 46%)",
  border: "hsl(140, 8%, 88%)",
  borderBright: "hsl(140, 8%, 82%)",
  tableHeaderFg: "hsl(150, 8%, 52%)",
};

const OUTCOME_COLORS = {
  signed_up: { base: "hsl(199, 89%, 60%)", text: "hsl(199, 70%, 35%)" },
  applied: { base: "hsl(217, 91%, 55%)", text: "hsl(217, 70%, 30%)" },
  callback: { base: "hsl(38, 92%, 50%)", text: "hsl(38, 70%, 30%)" },
  follow_up_today: { base: "hsl(38, 92%, 50%)", text: "hsl(38, 70%, 30%)" },
  follow_up_later: { base: "hsl(38, 92%, 50%)", text: "hsl(38, 70%, 30%)" },
  to_be_checked: { base: "hsl(270, 60%, 60%)", text: "hsl(270, 50%, 35%)" },
  voicemail: { base: "hsl(215, 16%, 57%)", text: "hsl(215, 16%, 37%)" },
  no_answer: { base: "hsl(215, 25%, 72%)", text: "hsl(215, 20%, 42%)" },
  not_interested: { base: "hsl(0, 72%, 51%)", text: "hsl(0, 72%, 35%)" },
  no_outcome: { base: COLORS.border, text: COLORS.mutedForeground },
};

const DONUT_KEYS = [
  { key: "signed_up", label: "Sign ups" },
  { key: "applied", label: "Applied" },
  { key: "callback", label: "Callback" },
  { key: "to_be_checked", label: "To be checked" },
  { key: "voicemail", label: "Voicemail" },
  { key: "no_answer", label: "No answer" },
  { key: "not_interested", label: "Not interested" },
  { key: "no_outcome", label: "No outcome" },
];

const FILTER_CHIPS = [
  { key: "all", label: "All" },
  { key: "overdue", label: "Overdue", dot: "#E24B4A" },
  { key: "not_contacted", label: "Not contacted" },
  { key: "signed_up", label: "Sign ups", dot: "hsl(199, 89%, 60%)" },
  { key: "applied", label: "Applied", dot: "hsl(217, 91%, 55%)" },
  { key: "callback", label: "Callback", dot: "hsl(38, 92%, 50%)" },
  { key: "to_be_checked", label: "To be checked", dot: "hsl(270, 60%, 60%)" },
  { key: "voicemail", label: "Voicemail", dot: "hsl(215, 16%, 57%)" },
  { key: "no_answer", label: "No answer", dot: "hsl(215, 25%, 72%)" },
  { key: "not_interested", label: "Not interested", dot: "hsl(0, 72%, 51%)" },
];

function humanizeOutcome(action) {
  if (!action) return "No outcome";
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isOverdue(profile) {
  if (!profile.follow_up_at) return false;
  const action = profile.last_action;
  if (action !== "follow_up_today" && action !== "follow_up_later") return false;
  return new Date(profile.follow_up_at) < new Date();
}

function isUncontacted(profile) {
  return !profile.last_action || profile.last_action === "not_contacted";
}

function relativeTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function sortProfiles(profiles) {
  return [...profiles].sort((a, b) => {
    const aOverdue = isOverdue(a) ? 0 : 1;
    const bOverdue = isOverdue(b) ? 0 : 1;
    if (aOverdue !== bOverdue) return aOverdue - bOverdue;
    const aUncontacted = isUncontacted(a) ? 0 : 1;
    const bUncontacted = isUncontacted(b) ? 0 : 1;
    return aUncontacted - bUncontacted;
  });
}

const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const { label, count, pct } = payload[0].payload;
  return (
    <Box sx={{ bgcolor: COLORS.foreground, color: "#fff", px: 1.5, py: 0.75, borderRadius: "6px", fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
      {label}: {count} ({pct}%)
    </Box>
  );
};

const DonutChart = ({ donutData }) => {
  const [activeKey, setActiveKey] = useState(null);

  const segments = useMemo(() => {
    const raw = { ...donutData };
    const callbackCount = (raw.follow_up_today || 0) + (raw.follow_up_later || 0);
    delete raw.follow_up_today;
    delete raw.follow_up_later;
    raw.callback = callbackCount;

    return DONUT_KEYS.map(({ key, label }) => ({
      key,
      label,
      count: raw[key] || 0,
      color: (OUTCOME_COLORS[key] || OUTCOME_COLORS.no_outcome).base,
    })).filter((s) => s.count > 0);
  }, [donutData]);

  const total = segments.reduce((s, seg) => s + seg.count, 0);

  const chartData = useMemo(() =>
    segments.map((seg) => ({
      ...seg,
      pct: total > 0 ? ((seg.count / total) * 100).toFixed(1) : "0",
    })),
  [segments, total]);

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 3, border: `1px solid ${COLORS.border}`, borderRadius: "0.75rem", bgcolor: COLORS.bg2, px: 2.5, py: 2 }}>
      <Box sx={{ width: 120, height: 120, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              cx="50%"
              cy="50%"
              outerRadius={58}
              innerRadius={34}
              strokeWidth={2}
              stroke="#fff"
              startAngle={90}
              endAngle={-270}
              onMouseEnter={(_, idx) => setActiveKey(chartData[idx]?.key)}
              onMouseLeave={() => setActiveKey(null)}
            >
              {chartData.map((seg) => (
                <Cell
                  key={seg.key}
                  fill={seg.color}
                  opacity={activeKey !== null && activeKey !== seg.key ? 0.55 : 1}
                  style={{ transition: "opacity 0.2s", cursor: "pointer" }}
                />
              ))}
            </Pie>
            <ReTooltip content={<DonutTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: 22, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: COLORS.foreground, lineHeight: 1 }}>{total}</Typography>
          <Typography sx={{ fontSize: 9, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground, lineHeight: 1.4 }}>profiles</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
        {DONUT_KEYS.map(({ key, label }) => {
          const seg = chartData.find((s) => s.key === key);
          const count = seg?.count || 0;
          return (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderRadius: "4px",
                px: 0.5,
                py: 0.15,
                "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
              }}
              onMouseEnter={() => setActiveKey(key)}
              onMouseLeave={() => setActiveKey(null)}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: (OUTCOME_COLORS[key] || OUTCOME_COLORS.no_outcome).base, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.foreground, width: 100 }}>{label}</Typography>
              <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: COLORS.foreground, minWidth: 20, textAlign: "right" }}>{count}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const PctCell = ({ count, prevCount, pct }) => {
  const barColor = `hsl(150, ${8 + (pct / 100) * 32}%, ${78 - (pct / 100) * 38}%)`;
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.foreground }}>
        {count} / {prevCount}{" "}
        <Typography component="span" sx={{ fontSize: 11, color: COLORS.mutedForeground }}>({pct.toFixed(1)}%)</Typography>
      </Typography>
      <Box sx={{ mt: 0.3, height: 4, borderRadius: 2, bgcolor: COLORS.bg3, overflow: "hidden", maxWidth: 120 }}>
        <Box sx={{ height: "100%", width: `${Math.min(pct, 100)}%`, borderRadius: 2, bgcolor: barColor, transition: "width 0.3s ease" }} />
      </Box>
    </Box>
  );
};

const ConvPill = ({ pct }) => {
  let bg, color;
  if (pct >= 60) { bg = "#EAF3DE"; color = "#27500A"; }
  else if (pct >= 40) { bg = "#FAEEDA"; color = "#633806"; }
  else { bg = "#FCEBEB"; color = "#791F1F"; }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", bgcolor: bg, color, borderRadius: "999px", px: 1.2, py: 0.2, fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
      {pct.toFixed(1)}%
    </Box>
  );
};

const RecruiterBreakdownTable = ({ userBreakdown }) => {
  const [showAll, setShowAll] = useState(false);

  const pipelineTotals = useMemo(() => {
    if (!userBreakdown?.length) return null;
    const contacted = userBreakdown.reduce((s, u) => s + (u.contacted || 0), 0);
    const totalAssigned = userBreakdown.reduce((s, u) => s + (u.total_assigned || 0), 0);
    const signedUp = userBreakdown.reduce((s, u) => s + (u.signed_up || 0), 0);
    const applied = userBreakdown.reduce((s, u) => s + (u.applied || 0), 0);
    const signRate = contacted > 0 ? (signedUp / contacted) * 100 : 0;
    const appliedRate = signedUp > 0 ? (applied / signedUp) * 100 : 0;
    return { contacted, totalAssigned, signedUp, applied, signRate, appliedRate };
  }, [userBreakdown]);

  const cols = [
    { label: "Recruiter", width: "20%" },
    { label: "Contacted", width: "16%" },
    { label: "Signed up", width: "16%" },
    { label: "Placed", width: "16%" },
    { label: "Sign rate", width: "16%" },
    { label: "Applied rate", width: "16%" },
  ];

  return (
    <Box sx={{ flex: 1, border: `1px solid ${COLORS.border}`, borderRadius: "0.75rem", bgcolor: COLORS.bg2, overflow: "hidden" }}>
      <Box sx={{ display: "flex", borderBottom: `0.5px solid ${COLORS.border}`, px: 2, py: 1 }}>
        {cols.map((c) => (
          <Typography key={c.label} sx={{ width: c.width, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.tableHeaderFg, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {c.label}
          </Typography>
        ))}
      </Box>
      {(showAll ? (userBreakdown || []) : (userBreakdown || []).slice(0, 7)).map((row) => {
        const signRate = row.sign_rate ?? (row.contacted > 0 ? (row.signed_up / row.contacted) * 100 : 0);
        const appliedRate = row.applied_rate ?? (row.signed_up > 0 ? (row.applied / row.signed_up) * 100 : 0);
        return (
          <Box key={row.internal_user?.id} sx={{ display: "flex", alignItems: "center", px: 2, py: 1.2, borderBottom: `0.5px solid ${COLORS.border}` }}>
            <Box sx={{ width: "20%", display: "flex", alignItems: "center", gap: 1 }}>
              <UserAvatar user={row.internal_user} size={24} />
              <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: COLORS.foreground }} noWrap>{row.internal_user?.full_name}</Typography>
            </Box>
            <Box sx={{ width: "16%" }}>
              <PctCell count={row.contacted || 0} prevCount={row.total_assigned || 0} pct={row.total_assigned > 0 ? ((row.contacted / row.total_assigned) * 100) : 0} />
            </Box>
            <Box sx={{ width: "16%" }}>
              <PctCell count={row.signed_up || 0} prevCount={row.contacted || 0} pct={row.contacted > 0 ? ((row.signed_up / row.contacted) * 100) : 0} />
            </Box>
            <Box sx={{ width: "16%" }}>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground }}>—</Typography>
            </Box>
            <Box sx={{ width: "16%" }}>
              <ConvPill pct={signRate} />
            </Box>
            <Box sx={{ width: "16%" }}>
              <ConvPill pct={appliedRate} />
            </Box>
          </Box>
        );
      })}
      {pipelineTotals && (
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.2, borderTop: `1px solid ${COLORS.borderBright}`, bgcolor: COLORS.bg2 }}>
          <Typography sx={{ width: "20%", fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: COLORS.tableHeaderFg }}>Pipeline total</Typography>
          <Box sx={{ width: "16%" }}>
            <PctCell count={pipelineTotals.contacted} prevCount={pipelineTotals.totalAssigned} pct={pipelineTotals.totalAssigned > 0 ? (pipelineTotals.contacted / pipelineTotals.totalAssigned) * 100 : 0} />
          </Box>
          <Box sx={{ width: "16%" }}>
            <PctCell count={pipelineTotals.signedUp} prevCount={pipelineTotals.contacted} pct={pipelineTotals.contacted > 0 ? (pipelineTotals.signedUp / pipelineTotals.contacted) * 100 : 0} />
          </Box>
          <Box sx={{ width: "16%" }}>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground }}>—</Typography>
          </Box>
          <Box sx={{ width: "16%" }}>
            <ConvPill pct={pipelineTotals.signRate} />
          </Box>
          <Box sx={{ width: "16%" }}>
            <ConvPill pct={pipelineTotals.appliedRate} />
          </Box>
        </Box>
      )}
      {(userBreakdown || []).length > 7 && (
        <Box
          component="button"
          onClick={() => setShowAll((v) => !v)}
          sx={{
            all: "unset",
            width: "100%",
            textAlign: "center",
            py: "10px",
            fontSize: 11,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: COLORS.mutedForeground,
            cursor: "pointer",
            borderTop: `0.5px solid ${COLORS.border}`,
            transition: "color 150ms ease",
            "&:hover": { color: COLORS.foreground },
          }}
        >
          {showAll ? "Show less" : `Show all ${(userBreakdown || []).length} recruiters`}
        </Box>
      )}
    </Box>
  );
};

const OutcomePill = ({ action }) => {
  const key = action || "no_outcome";
  const colors = OUTCOME_COLORS[key] || OUTCOME_COLORS.no_outcome;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "999px",
        px: 1.2,
        py: 0.25,
        bgcolor: `color-mix(in srgb, ${colors.base} 9%, transparent)`,
        color: colors.text,
        fontSize: 11,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        whiteSpace: "nowrap",
      }}
    >
      {humanizeOutcome(action)}
    </Box>
  );
};

const CoordinatorPipelineTrackingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.current_internal_user);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pipeline, setPipeline] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);

  const [activeFilter, setActiveFilter] = useState("all");
  const [recruiterFilter, setRecruiterFilter] = useState("all");
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  const authenticity_token = useSelector((state) => state.auth?.authenticity_token);
  const selectedProfile = useMemo(
    () => profiles.find((p) => String(p.id) === String(selectedProfileId)) || null,
    [profiles, selectedProfileId],
  );

  const handleLogOutcome = useCallback(
    (outcome, time, notes, followUpAt) => {
      if (!selectedProfile) return;
      const callLogPayload = { outcome, profile_pipeline_id: id };
      if (followUpAt) {
        callLogPayload.follow_up_at = followUpAt;
      } else if (time && outcome === "follow_up_today") {
        const [h, m] = time.split(":");
        const date = new Date();
        date.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
        callLogPayload.follow_up_at = date.toISOString();
      }
      if (notes) callLogPayload.notes = notes;
      sh.post(`/internal/external_profiles/${selectedProfile.id}/call_logs`, {
        authenticity_token,
        call_log: callLogPayload,
      }).then((res) => {
        const profile = res?.data?.profile;
        if (!profile) return;
        setProfiles((prev) => prev.map((item) => (String(item.id) === String(profile.id) ? profile : item)));
      });
    },
    [authenticity_token, selectedProfile, id],
  );

  const fetchData = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError("");

    Promise.all([
      sh.get(`/internal/profile_pipelines/${id}`),
      sh.get(`/internal/profile_pipelines/${id}/stats`),
    ])
      .then(([pipelineRes, statsRes]) => {
        const data = pipelineRes.data || {};
        setPipeline(data);
        setProfiles(data.external_profiles || []);
        setStats(statsRes.data || {});
      })
      .catch(() => setError("Failed to load pipeline data."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const overdueProfiles = useMemo(() => profiles.filter(isOverdue), [profiles]);

  const filterCounts = useMemo(() => {
    if (stats?.filter_counts) {
      const fc = { ...stats.filter_counts };
      if (!fc.callback && (fc.follow_up_today || fc.follow_up_later)) {
        fc.callback = (fc.follow_up_today || 0) + (fc.follow_up_later || 0);
      }
      return fc;
    }
    return {};
  }, [stats]);

  const filteredProfiles = useMemo(() => {
    let list = profiles;

    if (activeFilter !== "all") {
      if (activeFilter === "overdue") {
        list = list.filter(isOverdue);
      } else if (activeFilter === "not_contacted") {
        list = list.filter(isUncontacted);
      } else if (activeFilter === "callback") {
        list = list.filter((p) => p.last_action === "follow_up_today" || p.last_action === "follow_up_later");
      } else {
        list = list.filter((p) => p.last_action === activeFilter);
      }
    }

    if (recruiterFilter !== "all") {
      list = list.filter((p) => {
        const lastLog = p.call_logs?.[p.call_logs.length - 1];
        return lastLog && String(lastLog.recruiter_email) === String(recruiterFilter);
      });
    }

    return sortProfiles(list);
  }, [profiles, activeFilter, recruiterFilter]);

  if (currentUser && currentUser.role !== "admin" && currentUser.role !== "coordinator") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: COLORS.background }}>
        <Alert severity="error">Access denied. You must be a coordinator or admin.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: COLORS.background }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !pipeline) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: COLORS.background, p: 4 }}>
        <Alert severity="error">{error || "Pipeline not found."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.background }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", p: "32px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton size="small" onClick={() => navigate("/internal/coordinator-dashboard")} sx={{ color: COLORS.foreground }}>
              <ArrowLeft size={18} />
            </IconButton>
            <Box>
              <Typography sx={{ fontSize: 15, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: COLORS.foreground }}>{pipeline.title}</Typography>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground }}>{profiles.length} profiles</Typography>
            </Box>
          </Box>
          {overdueProfiles.length > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.8,
                bgcolor: "#FCEBEB",
                color: "#791F1F",
                borderRadius: "999px",
                px: 1.5,
                py: 0.5,
                fontSize: 12,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#E24B4A",
                  animation: "pulse-dot 1.5s ease-in-out infinite",
                  "@keyframes pulse-dot": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(1.3)" },
                  },
                }}
              />
              {overdueProfiles.length} overdue
            </Box>
          )}
        </Box>

        {/* Donut + Recruiter Breakdown */}
        {stats && (
          <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
            <DonutChart donutData={stats.donut || {}} />
            <RecruiterBreakdownTable userBreakdown={stats.user_breakdown} funnel={stats.funnel} />
          </Box>
        )}

        {/* Filter Chips */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {FILTER_CHIPS.map((chip) => {
            const isActive = activeFilter === chip.key;
            const count = filterCounts[chip.key] ?? (chip.key === "all" ? profiles.length : 0);
            return (
              <Button
                key={chip.key}
                disableRipple
                onClick={() => setActiveFilter(chip.key)}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  height: 30,
                  px: 1.5,
                  minWidth: 0,
                  fontSize: 12,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  border: isActive ? "1px solid #534AB7" : "0.5px solid transparent",
                  bgcolor: isActive ? "#EEEDFE" : "white",
                  color: isActive ? "#3C3489" : COLORS.mutedForeground,
                  gap: 0.6,
                  "&:hover": { bgcolor: isActive ? "#EEEDFE" : "rgba(0,0,0,0.03)" },
                }}
              >
                {chip.dot && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: chip.dot, flexShrink: 0 }} />}
                {chip.label}
                <Typography component="span" sx={{ fontSize: 10, opacity: 0.7, fontFamily: "Segoe UI Regular, sans-serif, system-ui", ml: 0.3 }}>{count}</Typography>
              </Button>
            );
          })}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground }}>Recruiter:</Typography>
            <Select
              value={recruiterFilter}
              onChange={(e) => setRecruiterFilter(e.target.value)}
              size="small"
              sx={{
                fontSize: 12,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                height: 30,
                minWidth: 120,
                borderRadius: "8px",
                bgcolor: COLORS.bg2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.borderBright },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: 12 }}>All</MenuItem>
              {(pipeline.assigned_users || []).map((u) => (
                <MenuItem key={u.id} value={u.email} sx={{ fontSize: 12 }}>
                  {u.full_name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Candidate Table */}
        <Box sx={{ bgcolor: COLORS.bg2, border: `1px solid ${COLORS.border}`, borderRadius: "8px", overflow: "hidden" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 130px 90px",
              px: 2,
              py: 1,
              bgcolor: COLORS.bg2,
              borderBottom: `0.5px solid ${COLORS.border}`,
            }}
          >
            {["Profile", "Last action", "Last activity"].map((h) => (
              <Typography key={h} sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.tableHeaderFg, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                {h}
              </Typography>
            ))}
          </Box>

          {filteredProfiles.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground }}>No profiles match this filter.</Typography>
            </Box>
          ) : (
            filteredProfiles.map((profile) => {
              const overdue = isOverdue(profile);
              const lastLog = profile.call_logs?.[profile.call_logs.length - 1];
              const lastActivity = lastLog?.created_at;

              return (
                <React.Fragment key={profile.id}>
                  <Box
                    onClick={() => setSelectedProfileId(profile.id)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 130px 90px",
                      px: 2,
                      py: 1.2,
                      alignItems: "center",
                      borderBottom: `0.5px solid ${COLORS.border}`,
                      borderLeft: overdue ? "2.5px solid #E24B4A" : "2.5px solid transparent",
                      cursor: "pointer",
                      transition: "background-color 0.15s",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                    }}
                  >
                    {/* Profile */}
                    <Box>
                      <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: COLORS.foreground }}>
                        {[profile.first_name, profile.last_name].filter(Boolean).join(" ") || "Unnamed"}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: COLORS.mutedForeground }}>{profile.desired_title || "—"}</Typography>
                    </Box>

                    {/* Last action */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <OutcomePill action={profile.last_action} />
                    </Box>

                    {/* Last activity */}
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: overdue ? "#A32D2D" : COLORS.mutedForeground,
                        fontWeight: overdue ? 500 : 400,
                      }}
                    >
                      {relativeTime(lastActivity)}
                    </Typography>

                  </Box>
                </React.Fragment>
              );
            })
          )}
        </Box>
      </Box>

      {/* Profile detail drawer */}
      <Drawer
        anchor="right"
        open={!!selectedProfile}
        onClose={() => setSelectedProfileId(null)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, bgcolor: "hsl(140, 10%, 95%)" } }}
      >
        {selectedProfile && (
          <Box sx={{ p: 3, height: "100%", overflowY: "auto", "--input-bg": "white" }}>
            <ProfileDetailPanel
              profile={selectedProfile}
              onLogOutcome={handleLogOutcome}
              onSignUpToCandidate={() => {}}
              onArchive={() => setSelectedProfileId(null)}
            />
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default CoordinatorPipelineTrackingPage;
